import React, { useRef, useState } from 'react'
import {
  CAlert,
  CButton,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CNav,
  CNavItem,
  CNavLink,
} from '@coreui/react'
import { useBostonState, emit } from '../lib/useBoston'
import { apiFetch } from '../lib/api'
import { API_TABS, SABRE_CMD_CATALOG } from '../lib/catalogs'
import { generateFakeBookingData } from '../lib/builder'
import { useUI } from '../../../shared/ui/UIProvider'

/* Port fiel de viewApis/bindApis de boston_ia (SPA vanilla):
   - Tabs visibles segun API_TABS del original: "Crear reserva (vuelo + hotel + auto)" y "Recuperar PNR".
   - Se portan tambien las pestañas auth / booking / create del original (renderApiTab las soporta
     igual que alla: solo se alcanzan si state.apisTab se setea programaticamente).
   - El flujo "Reserva GDS completa" ejecuta la secuencia nativa Sabre paso a paso, con skip de los
     sells cuando el shopper (HOT* / CF*) fallo y recovery automatico con ET cuando ER queda pendiente. */

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"

/* ===========================================================================
   Secuencia nativa Sabre (logica original intacta)
   ===========================================================================
   Secuencia nativa Sabre, segun documentacion oficial:
     1. Disponibilidad aerea -> 1<fecha><orig><dest>
     2. Venta -> 0<cant><clase>1 (linea 1 de la disponibilidad)
     3. Hotel disponibilidad -> HOT<checkin>-<checkout><ciudad>
     4. Hotel pasivo -> 0HHL<chain>GK<rooms><city><in>-<out>/RG-<chain>
     5. Auto disponibilidad -> CF<pickup>-<return><city>
     6. Auto pasivo -> 0CCR<vendor><pickup>-<return><city>GK1/<cat>
     7. Nombre -> -<APELLIDO>/<NOMBRE> <TITULO>
     8. Telefono -> 9<ciudad><numero>-<tipo>
     9. Received from -> 6<quien autoriza>
     10. Ticketing -> 7TAW/
     11. WP (cotizar)
     12. ER (guarda y devuelve record locator)
   Secuencia oficial Sabre (basada en la guia v.26.4 Agency Workspace):
     1. I                                ← limpia AAA
     2. 1<fecha><o><d>[¥<aerolinea>]    ← disponibilidad ida
     3. 01Y1                              ← vende linea 1 (sell from shopper)
     4. (opcional) 1<rdate><d><o>[¥AL]  ← disp vuelta
     5. (opcional) 01Y1                   ← vende vuelta
     6. (opcional) HOT1                   ← hotel smart: toma city+fechas del seg 1
     7. (opcional) 0H1#1                  ← vende habitacion linea 1 del shopper
     8. (opcional) CF1/2 (o CF1)          ← car smart: usa seg 1 (y 2 si return)
     9. (opcional) 0C1                    ← vende auto linea 1 del shopper
    10. -1<APELLIDO>/<NOMBRE><TITULO>    ← name (1=count, sin espacio)
    11. 9<CITY><TEL>-A                   ← phone (A=agencia)
    12. 7TAW/                              ← ticketing time limit
    13. 6<RECVD>                          ← received from
    14. ER                                 ← end and retrieve, devuelve locator */
function frSequence(d) {
  const seq = []
  seq.push('I') // 1. limpia AAA
  // VUELO ida
  const airline = d.airline ? `¥${d.airline}` : ''
  seq.push(`1${d.date}${d.origin}${d.dest}${d.time || ''}${airline}`.trim())
  seq.push(`0${d.seats}${d.cls}1`)
  // VUELO vuelta (opcional)
  const hasReturn = !!(d.rdate && d.rdate.length >= 5)
  if (hasReturn) {
    seq.push(`1${d.rdate}${d.dest}${d.origin}${airline}`)
    seq.push(`0${d.seats}${d.cls}1`)
  }
  // HOTEL smart: HOT1 toma ciudad+fechas del segmento aereo 1.
  // Asume que el agente esta yendo al destino del vuelo y se queda
  // hasta el segmento de regreso (o sino default 1 noche).
  if (d.hotel) {
    seq.push('HOT1')
    seq.push('0H1#1') // vende linea 1 del shopper
  }
  // AUTO smart: CF1/2 si tenemos vuelta, sino CF1
  if (d.car) {
    seq.push(hasReturn ? 'CF1/2' : 'CF1')
    seq.push('0C1') // vende linea 1 del shopper
  }
  // NAME: formato correcto -1LNAME/FNAMETITLE (1=count, sin espacios)
  seq.push(`-${d.seats}${d.lname}/${d.fname}${d.title}`)
  // CELULAR del pax PRIMERO (tipo H, 11xxxxxxxx): queda como phone #1 para la precarga
  if (d.celular) seq.push(`9${d.celular}-H`)
  // EMAIL del pax (PE)
  if (d.email) seq.push(`PE¥${d.email}`)
  // PHONE: tipo A (agencia)
  seq.push(`9${d.phCity || d.origin}${d.phone}-A`)
  // ADDRESS: muchas PCC piden W- antes de ER (NEED ADDRESS)
  seq.push(`W-${d.addr || 'AV CORRIENTES 1234 BUENOS AIRES AR'}`)
  // DOCS / APIS: el formato simple sin carrier+HK tira "SEGMENT NUMBER NOT
  // VALID" porque Sabre no sabe a que segmentos aplicar. Necesita carrier
  // (YY=universal) y HK<n> para que infiera los segmentos del PNR.
  // FORMATO OFICIAL Sabre Red Crypto:
  //   3DOCS<carrier>/HK<n>-P/<emisor>/<num>/<nac>/<dob>/<sex>/<exp>/<APELLIDO>/<NOMBRE>-<pax>
  // Ejemplo: 3DOCSYY/HK1-P/ARG/AB123456/ARG/15FEB90/M/15DEC30/SILVA/LAURA-1.1
  if (d.docsOn) {
    const carrier = d.docsCarrier || 'YY'
    const docsCmd = `3DOCS${carrier}/HK${d.seats}-P/${d.docsCountry}/${d.docsPassport}/${d.docsCountry}/${d.docsDob}/${d.docsSex}/${d.docsExp}/${d.lname}/${d.fname}-1.1`
    seq.push(docsCmd)
  }
  // REMARKS con datos del pax — alimentan la precarga por coincidence/locator
  if (d.dni) seq.push(`5H-DNI ${d.dni}`)
  if (d.cuil) seq.push(`5H-CUIL ${d.cuil}`)
  if (d.addr) seq.push(`5H-DIRECCION ${d.addr}`)
  if (d.docsDob) seq.push(`5H-FECHA NAC ${d.docsDob}`)
  // TICKETING TIME LIMIT
  seq.push('7TAW/')
  // RECEIVED FROM
  seq.push(`6${d.rcvd}`)
  // END AND RETRIEVE
  seq.push('ER')
  return seq
}

/* ---------------------------------------------------------------------
   Extractor de record locator endurecido.
   El PNR es exactamente 6 chars alfanumericos en MAYUSCULAS, suele empezar
   con letra. Pero "GARCIA", "PEREZ", "MARTIN" y nombres de ciudades como
   "MIAMIA" pueden confundirse. Por eso construimos una BLACKLIST con los
   valores que el usuario tipeó en el form para descartarlos.
   --------------------------------------------------------------------- */
function extractPnrFromResponse(txt, blacklist) {
  if (!txt) return null
  // Si la respuesta contiene un error claro de Sabre, NO hay PNR para extraer.
  // Evita devolver "NUMBER" de "NUMBER OF NAMES NOT EQUAL TO RESERVATIONS",
  // "FORMAT" de "FORMAT ERROR", etc.
  const errorPatterns = [
    /NUMBER\s+OF\s+NAMES\s+NOT\s+EQUAL/i,
    /UNABLE\s+TO/i,
    /INVALID\s+(?:FORMAT|ENTRY|COMMAND)/i,
    /FORMAT\s+ERROR/i,
    /FORMAT\s+NO\s+LONGER\s+SUPPORTED/i,
    /MIGRATE\s+TO\s+CONTENT\s+SERVICES/i,
    /CHECK\s+SEGMENT/i,
    /ITINERARY\s+REQUIRED/i,
    /COMPLETE\s+TRANSACTION/i,
    /RESTRICTED/i,
    /NOT\s+AUTHORIZED/i,
    /NO\s+(?:RECORD|PNR|ITINERARY|AGREEMENT|NAMES?)\b/i,
    /CANNOT\s+(?:END|SAVE|PROCESS)/i,
    /MISSING/i,
    /REQUIRED/i,
    /CHECK\s+(?:NAME|ENTRY|FORMAT)/i,
    /PNR\s*EXPIRED/i,
    /SYSTEM\s+(?:DOWN|UNAVAILABLE)/i,
    /NEED\s+(?:ADDRESS|PHONE|NAME|RECVD|TKT)/i,
    /USE\s+W-/i,
    /ADDRESS\s+REQUIRED/i,
    /PHONE\s+(?:REQUIRED|MISSING)/i,
    /PSGR\s+SECURITY\s+DATA/i,
    /PASSENGER\s+(?:SECURITY|DATA)/i,
    /UPDATE\s+AND\s+RETRY/i,
    /APIS\s+(?:REQUIRED|MISSING)/i,
    /DOCS\s+REQUIRED/i,
    /REQUEST\s+PENDING/i,
    /SEND\s+VIA\s+TELETYPE/i,
    /DIRECT\s+CONNECT\s+NOT\s+PROCESSED/i,
    /SEGMENT\s+NUMBER\s+NOT\s+VALID/i,
  ]
  for (const re of errorPatterns) {
    if (re.test(txt)) return null
  }
  const bl = new Set((blacklist || []).filter(Boolean).map((x) => String(x).toUpperCase().trim()))
  // Palabras comunes que NO son PNRs (vienen de respuestas Sabre estandar)
  const stopwords = new Set([
    'FINISH','TICKET','NEEDED','EZEMIA','FROMTO','ARRIVE','DEPART','STATUS',
    'CARRIER','PERSON','MASTER','HOTEL','RENTAL','HOLIDAY','MARRIOTT',
    'SHERATON','PASSPORT','AIRPORT','RECORD','LOCATOR','RESERV','MIAMI',
    'BUENOS','MADRID','LONDON','BERLIN','TAIPEI','SYDNEY','SANTOS',
    'AMERICAN','UNITED','DELTA','ROUTING','NUMBER','NAMES','EQUAL',
    'FORMAT','ERROR','UNABLE','INVALID','UPLOAD','UPDATE','RETURN',
    'RESPON','RESERV','OFFICE','REGION','SECTOR','SERVER','CLIENT',
    'PUBLIC','BOOKED','EXPIRE','EXPIR','SABRE','SYSTEM','SEGMENT',
    'FORWARD','REPLY','REVIEW','RESULT','TRAVEL','BRANCH','MEMBER',
    'COMBI','CLASSE','SECTOR','GLOBAL','PASS','RETAIL','SOURCE',
    'LONGER','SUPPORT','LODGNG','LODGING','MIGRAT','MIGRATE','SERVIC',
    'SERVICE','CONTENT','SUPPLY','SUPPLI','REQUIR','CHANGE','MODIFI',
    'SELECT','OPTION','BUTTON','SUBMIT','CANCEL','DETAIL','HEADER',
    'FOOTER','UPLOAD','REMOVE','DELETE','INSERT','UPDATE','DOMAIN',
    'PHONES','EMAILS','REMARK','PASSEN','SECURI','SECRET','RETURN',
    'RETRYS','RETRYE','UPLOAD','DOCUMA','DOCUME','DOCUMS','DOCALL',
    'PROCES','PROCSS','DIRECT','CONECT','CONNCT','THROUG','TIMEOU',
    'OFFER','OFFERS','LATAM','AAVANC','AVIANC','UNITED','AMERIC',
    'ORIGIN','SOURCE','BRANCH','MARKET','ROUTER','VECTOR','MATRIX',
    'RESEND','TELETY','PENDIN','REQUES','TYPECN','CARBON','MOMENT',
    'WAIT','WAITNG','LATER','BEFORE','AFTER','AGAIN','REPRES','REPRY',
    'CHARGE','UNUSED','SAVENB','CHANNE','METHOD','EXTRAS','MANUAL',
  ])
  const isValid = (code) => {
    if (!code) return false
    const c = code.toUpperCase()
    if (c.length !== 6) return false
    if (!/^[A-Z][A-Z0-9]{5}$/.test(c)) return false
    if (bl.has(c)) return false
    if (stopwords.has(c)) return false
    // CURRENCY/AMOUNT: USD655, ARS936, EUR123, BRL280, etc.
    //   3 letras (codigo moneda ISO) + 3 digitos
    if (/^(?:USD|ARS|EUR|BRL|CLP|COP|MXN|PEN|UYU|GBP|JPY|CAD|AUD|CHF|CNY)\d{3}$/i.test(c)) return false
    // Cualquier 6char con MAS de 3 digitos seguidos NO es un PNR Sabre tipico
    // (PNRs reales suelen ser tipo ABC123, AB1C2D, mezclando letras y numeros)
    if (/\d{4,}/.test(c)) return false
    // Si arranca con muchos digitos seguidos al medio, descartar
    if (/^[A-Z]\d{5}$/.test(c)) return false
    // Excluir si es una palabra "pura" del input (ej apellido sin numeros)
    if (/^[A-Z]+$/.test(c) && bl.size > 0) {
      for (const b of bl) {
        if (b.startsWith(c) || c.startsWith(b)) return false
      }
    }
    return true
  }
  // 1) JSON estructurado: campos tipicos de Sabre Web
  let m
  const jsonKeys = [
    'recordLocator','record_locator','confirmationId','confirmation_id',
    'pnrLocator','pnr_locator','locator','bookingReference','booking_reference',
    'pnr','reservationId','reservation_id',
  ]
  for (const k of jsonKeys) {
    const re = new RegExp('"' + k + '"\\s*:\\s*"([A-Z][A-Z0-9]{5})"', 'i')
    m = txt.match(re)
    if (m && isValid(m[1])) return m[1]
  }
  // 2) Linea tipo "RP/PCC/  USR  AA/SU 26MAY26/1234Z   ABCDEF"
  m = txt.match(/RP\/[^\n]+?([A-Z][A-Z0-9]{5})\b/)
  if (m && isValid(m[1])) return m[1]
  // 3) "**<PNR>**" o "**ABCDEF" (host emulator)
  m = txt.match(/\*\*([A-Z][A-Z0-9]{5})\b/)
  if (m && isValid(m[1])) return m[1]
  // 4) "PNR ABCDEF" o "RECORD LOCATOR ABCDEF" o "LOCATOR: ABCDEF"
  m = txt.match(/(?:PNR|LOCATOR|RECORD\s+LOCATOR)[:\s]+([A-Z][A-Z0-9]{5})\b/i)
  if (m && isValid(m[1])) return m[1]
  // 5) "ABCDEF FINISH" / "ABCDEF OK" (cierre clasico)
  m = txt.match(/\b([A-Z][A-Z0-9]{5})\s+(?:FINISH|END\s+TRANSACTION|OK\s+\d|FIN)/i)
  if (m && isValid(m[1])) return m[1]
  // 6) "*ABCDEF" en el response (re-display tras ER)
  m = txt.match(/\*([A-Z][A-Z0-9]{5})\b/)
  if (m && isValid(m[1])) return m[1]
  // 7) Último recurso: escanear TODAS las cadenas de 6 alfanumericos válidos
  //    y devolver la PRIMERA que pase los filtros y NO esté en la blacklist.
  const all = txt.match(/\b[A-Z][A-Z0-9]{5}\b/g) || []
  for (const c of all) {
    if (isValid(c)) return c
  }
  return null
}

/* ===== Helpers del form "Reserva GDS completa" ===== */

/** Valores iniciales del form a partir de los datos de fantasia (mismos defaults que el HTML original). */
function formFromFake(f) {
  return {
    // Vuelo
    date: f.date, rdate: f.hotelCheckout, origin: f.origin, dest: f.dest, time: f.time,
    airline: '', cls: f.cls, seats: f.seats,
    // Hotel
    hChain: f.hotelChain, hCity: f.hotelCity, hCheckin: f.hotelCheckin, hCheckout: f.hotelCheckout, hRooms: '1',
    // Auto
    cVendor: f.carVendor, cCity: f.carCity, cPickup: f.carPickup, cReturn: f.carReturn,
    cCat: f.carCategory, cPickupTime: f.carPickupTime, cReturnTime: f.carReturnTime,
    // Pax y contacto
    lname: f.lname, fname: f.fname, title: f.titulo, phone: f.phone, phCity: f.origin, rcvd: 'PAX',
    email: f.email, celular: f.celular, dni: f.dni, cuil: f.cuil, addr: f.addr,
    // DOCS / APIS
    docsOn: true, docsCountry: f.docsCountry, docsPassport: f.docsPassport, docsSex: f.docsSex,
    docsDob: f.docsDob, docsExp: f.docsExp, docsCarrier: 'YY',
  }
}

/** Equivalente de frRead(): normaliza los valores del form igual que el original. */
function frRead(form, flags) {
  return {
    // Vuelo
    date: (form.date || '').trim().toUpperCase(),
    rdate: (form.rdate || '').trim().toUpperCase(), // fecha vuelta opcional
    origin: (form.origin || '').trim().toUpperCase(),
    dest: (form.dest || '').trim().toUpperCase(),
    time: (form.time || '').trim(),
    airline: (form.airline || '').trim().toUpperCase(), // filtro aerolinea opcional
    cls: (form.cls || '').trim().toUpperCase() || 'Y',
    seats: form.seats || '1',
    email: (form.email || '').trim(),
    celular: (form.celular || '').replace(/\D/g, ''),
    dni: (form.dni || '').replace(/\D/g, ''),
    cuil: (form.cuil || '').trim(),
    // Hotel
    hotel: !!flags.hotel,
    hChain: (form.hChain || '').trim().toUpperCase(),
    hCity: (form.hCity || '').trim().toUpperCase(),
    hCheckin: (form.hCheckin || '').trim().toUpperCase(),
    hCheckout: (form.hCheckout || '').trim().toUpperCase(),
    hRooms: form.hRooms || '1',
    // Auto
    car: !!flags.car,
    cVendor: (form.cVendor || '').trim().toUpperCase(),
    cCity: (form.cCity || '').trim().toUpperCase(),
    cPickup: (form.cPickup || '').trim().toUpperCase(),
    cReturn: (form.cReturn || '').trim().toUpperCase(),
    cCat: (form.cCat || '').trim().toUpperCase() || 'ECAR',
    cPickupTime: (form.cPickupTime || '').trim() || '1000',
    cReturnTime: (form.cReturnTime || '').trim() || '1000',
    // Pax y contacto
    lname: (form.lname || '').trim().toUpperCase().replace(/\s+/g, ''),
    fname: (form.fname || '').trim().toUpperCase().replace(/\s+/g, ''),
    title: ((form.title || '').trim().toUpperCase() || 'MR').replace(/\s+/g, ''),
    phone: (form.phone || '').trim(),
    phCity: (form.phCity || '').trim().toUpperCase(),
    rcvd: (form.rcvd || '').trim() || 'PAX',
    addr: ((form.addr || '').trim() || 'AV CORRIENTES 1234 BUENOS AIRES AR').toUpperCase(),
    // DOCS / APIS
    docsOn: !!form.docsOn,
    docsCountry: (form.docsCountry || 'ARG').trim().toUpperCase(),
    docsPassport: (form.docsPassport || 'AB123456').trim().toUpperCase(),
    docsSex: (form.docsSex || 'M').trim().toUpperCase(),
    docsDob: (form.docsDob || '15FEB90').trim().toUpperCase(),
    docsExp: (form.docsExp || '15DEC30').trim().toUpperCase(),
    docsCarrier: (form.docsCarrier || 'YY').trim().toUpperCase(),
  }
}

/* ===== Subcomponentes de presentacion del flujo "Reserva GDS completa" ===== */

/** Port de renderFrLog(): log compacto de los comandos ejecutados. */
function FrLog({ responses }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
        Log de {responses.length} comando(s) ejecutado(s)
      </div>
      <div
        style={{
          display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 300, overflowY: 'auto',
          border: '1px solid var(--border)', borderRadius: 6, padding: 8, background: '#0f172a',
        }}
      >
        {responses.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontFamily: MONO, fontSize: 11.5 }}>
            <span style={{ color: r.ok ? '#22d989' : '#dc2626', fontWeight: 600, minWidth: 18 }}>{i + 1}.</span>
            <code style={{ color: r.ok ? '#22d989' : '#dc2626', flexShrink: 0, minWidth: 180, fontWeight: 600 }}>{r.cmd}</code>
            <span style={{ color: '#cbd5e1', flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {String(r.txt || '').slice(0, 200)}
              {String(r.txt || '').length > 200 ? '...' : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Sugerencias contextuales según el error específico (mismos textos del original). */
function frAbortHint(txt) {
  const hintBox = { background: '#0f172a', color: '#fde68a', padding: '10px 12px', borderRadius: 6, marginTop: 10, fontSize: 12.5 }
  if (/PSGR\s+SECURITY\s+DATA|APIS|DOCS/i.test(txt)) {
    return (
      <div style={{ ...hintBox, fontFamily: MONO }}>
        <strong style={{ color: '#fbbf24' }}>💡 Vuelo internacional → APIS/DOCS requerido</strong>
        <br />
        Activá el checkbox <strong>"Incluir DOCS / APIS"</strong> en el form y dale 🎲 para generar datos fake validos. El
        comando se arma asi:
        <br />
        <code
          style={{
            background: 'rgba(34,217,137,.15)', color: '#22d989', padding: '2px 6px', borderRadius: 3,
            marginTop: 6, display: 'inline-block', wordBreak: 'break-all',
          }}
        >
          3DOCSYY/HK1-P/ARG/AB123456/ARG/15FEB90/M/15DEC30/SILVA/LAURA-1.1
        </code>
        <br />
        <span style={{ color: '#94a3b8', fontSize: 11 }}>
          {'Formato: 3DOCS<carrier>/HK<pax>-P/<pais_emisor>/<pasaporte>/<nacionalidad>/<DOB>/<sex>/<exp>/<apellido>/<nombre>-<pax>'}
        </span>
      </div>
    )
  }
  if (/NEED\s+ADDRESS|USE\s+W-/i.test(txt)) {
    return <div style={hintBox}>💡 Tu PCC exige W- (dirección). Verificá que el campo "Dirección" esté completo.</div>
  }
  if (/NUMBER\s+OF\s+NAMES/i.test(txt)) {
    return (
      <div style={hintBox}>
        💡 Tu AAA tiene PNRs huérfanos. Ejecutá <code>I</code> manualmente en la Consola GDS y volvé a probar.
      </div>
    )
  }
  if (/FORMAT\s+NO\s+LONGER\s+SUPPORTED/i.test(txt)) {
    return <div style={hintBox}>💡 Tu PCC migró a Content Services. Destildá Hotel/Auto, o usá la API moderna /sabre/enhancedAirBook.</div>
  }
  if (/REQUEST\s+PENDING|TELETYPE/i.test(txt)) {
    return (
      <div style={hintBox}>
        💡 <strong>Hay un segmento broken</strong> (probablemente el auto/hotel falló con DIRECT CONNECT). El PNR quedó{' '}
        <strong>pendiente</strong>.
        <br />
        <strong>Solución rápida:</strong> destildá Hotel y Auto en el form, dejá solo vuelo + DOCS, y volvé a mandar. Vas a
        tener PNR limpio.
        <br />
        <small style={{ color: '#94a3b8' }}>
          Alternativamente: andá a la Consola GDS y mandá <code>ET</code> manualmente para forzar el end-transaction por
          teletype.
        </small>
      </div>
    )
  }
  if (/SEGMENT\s+NUMBER\s+NOT\s+VALID/i.test(txt)) {
    return <div style={hintBox}>💡 Hay segmentos broken en el PNR. Destildá Hotel/Auto y reintentá solo con vuelo + DOCS.</div>
  }
  return null
}

/** Resultado del flujo (progreso / error / éxito / sin PNR), fiel al innerHTML del original. */
function FrResult({ run, toast }) {
  if (!run) return null

  if (run.phase === 'running') {
    return (
      <div>
        <div style={{ padding: 14, background: run.tone === 'warn' ? '#fef3c7' : '#f1f5f9', borderRadius: 6, marginTop: 14, fontSize: 13 }}>
          {run.banner}
        </div>
        {run.responses.length > 0 ? <FrLog responses={run.responses} /> : null}
      </div>
    )
  }

  if (run.phase === 'aborted') {
    const aborted = run.aborted
    return (
      <div>
        <div style={{ background: 'rgba(220,38,38,.12)', borderLeft: '3px solid #dc2626', padding: 14, borderRadius: 6, marginTop: 14 }}>
          <strong style={{ color: '#dc2626', fontSize: 15 }}>✗ La secuencia falló en el paso {run.responses.length}</strong>
          <div style={{ marginTop: 6, fontSize: 13 }}>
            El comando{' '}
            <code style={{ background: '#0f172a', color: '#fbbf24', padding: '3px 8px', borderRadius: 3 }}>{aborted.cmd}</code>{' '}
            devolvió:
          </div>
          <pre
            style={{
              background: '#0f172a', color: '#fca5a5', padding: '8px 12px', borderRadius: 4, marginTop: 8,
              fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 140, overflow: 'auto',
            }}
          >
            {(aborted.txt || '').slice(0, 600)}
          </pre>
          {frAbortHint(aborted.txt || '')}
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: 8 }}>
            Comandos ejecutados antes del error: {run.responses.length - 1}. Revisá el log abajo.
          </small>
        </div>
        <FrLog responses={run.responses} />
      </div>
    )
  }

  if (run.phase === 'success') {
    const pnr = run.pnr
    return (
      <div>
        <div
          style={{
            background: 'linear-gradient(135deg,#22d989,#18b774)', color: 'white', padding: 24, borderRadius: 8,
            marginTop: 14, boxShadow: '0 8px 20px rgba(34,217,137,.3)',
          }}
        >
          <div style={{ fontSize: 13, opacity: 0.9, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>
            ✓ Reserva creada
          </div>
          <div style={{ fontSize: 42, fontWeight: 800, margin: '8px 0', fontFamily: MONO, letterSpacing: '.02em' }}>{pnr}</div>
          <div style={{ fontSize: 13, opacity: 0.9, marginTop: 6 }}>
            <strong>Para consultar:</strong>{' '}
            <code style={{ background: 'rgba(0,0,0,.25)', padding: '3px 10px', borderRadius: 3, fontSize: 14 }}>*{pnr}</code>
            {'  '}
            <strong>Para reabrir:</strong>{' '}
            <code style={{ background: 'rgba(0,0,0,.25)', padding: '3px 10px', borderRadius: 3, fontSize: 14 }}>*{pnr}</code>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <CButton
              style={{ background: 'rgba(255,255,255,.2)', color: 'white', border: 0 }}
              onClick={() => apiFetch('GET', '/sabre/executeCommand?command=' + encodeURIComponent('*' + pnr))}
            >
              📖 Consultar reserva
            </CButton>
            <CButton
              style={{ background: 'rgba(255,255,255,.2)', color: 'white', border: 0 }}
              onClick={() => {
                if (navigator.clipboard) navigator.clipboard.writeText(pnr)
                toast('PNR copiado: ' + pnr, 'success')
              }}
            >
              📋 Copiar PNR
            </CButton>
          </div>
        </div>
        <FrLog responses={run.responses} />
      </div>
    )
  }

  // phase === 'nopnr'
  return (
    <div>
      <div style={{ background: 'rgba(217,119,6,.12)', borderLeft: '3px solid #d97706', padding: 14, borderRadius: 6, marginTop: 14 }}>
        <strong>⚠ Secuencia ejecutada pero no se detectó el PNR</strong>
        <small style={{ color: 'var(--muted)', display: 'block', marginTop: 4 }}>
          Los {run.responses.length} comandos se ejecutaron sin error pero el PNR no apareció en la respuesta del ER. Probá{' '}
          <code>*A</code> en la Consola GDS para ver el último PNR activo.
        </small>
      </div>
      <FrLog responses={run.responses} />
    </div>
  )
}

/* ===== Tab AUTH (port de apiTabAuth + bind btn-get-session) ===== */
function ApiTabAuth({ state }) {
  const ok = !!state.sessionToken
  return (
    <>
      <div className="card-x">
        <div className="bia-card-head">
          <h3>Sesion actual</h3>
          {ok ? <span className="bia-badge ok">🔐 Token activo</span> : <span className="bia-badge warn">⚠ Sin token</span>}
        </div>
        <p style={{ marginTop: 0 }}>
          El token operativo se gestiona desde la <strong>barra superior</strong> de la app. Todas las llamadas Sabre lo usan
          automaticamente.
        </p>
      </div>
      <div className="card-x">
        <h3>Validar sesion activa</h3>
        <p style={{ marginTop: 0 }}>
          Llama a <code>GET /customScripts/session</code> para verificar que el token + x-api-key son validos.
        </p>
        <CButton color="success" className="btn-brand" disabled={!ok} onClick={() => apiFetch('GET', '/customScripts/session')}>
          Validar sesion
        </CButton>
      </div>
    </>
  )
}

/* ===== Tab PNR (port de apiTabPnr + binds) ===== */
function ApiTabPnr({ state, toast }) {
  return (
    <div className="card-x">
      <h3>Lectura PNR</h3>
      <div className="mb-3">
        <CFormLabel>PNR (record locator)</CFormLabel>
        <CFormInput
          className="bia-mono"
          maxLength={6}
          placeholder="ABC123"
          value={state.apisPnr}
          onChange={(event) => {
            state.apisPnr = event.target.value.trim().toUpperCase()
            emit()
          }}
        />
      </div>
      <div className="d-flex flex-wrap gap-2">
        <CButton
          color="success"
          className="btn-brand"
          onClick={async () => {
            if (!state.apisPnr) {
              toast('Carga un PNR.', 'warning')
              return
            }
            await apiFetch('GET', `/sabre/reservation?pnr=${encodeURIComponent(state.apisPnr)}`)
          }}
        >
          GET /sabre/reservation
        </CButton>
        <CButton color="light" onClick={() => apiFetch('GET', `/sabre/freeTAS?pnr=${encodeURIComponent(state.apisPnr)}`)}>
          GET /sabre/freeTAS
        </CButton>
        <CButton
          color="light"
          onClick={() => {
            if (!state.apisPnr) return
            apiFetch('GET', `/sabre/executeCommand?command=${encodeURIComponent('*' + state.apisPnr)}`)
          }}
        >
          executeCommand *A
        </CButton>
      </div>
    </div>
  )
}

/* ===== Tab BOOKING (port de apiTabBooking + binds) ===== */
function ApiTabBooking({ state, toast }) {
  const [payload, setPayload] = useState(state.apisBookingPayload)

  async function postJson(path) {
    try {
      await apiFetch('POST', path, JSON.parse(state.apisBookingPayload))
    } catch (e) {
      toast('JSON invalido: ' + e.message, 'danger')
    }
  }

  return (
    <div className="card-x">
      <h3>Booking programatico (OTA)</h3>
      <CAlert color="warning">Para agregar segmentos al PNR. Requiere body OTA completo.</CAlert>
      <div className="mb-3">
        <CFormLabel>Payload OTA JSON</CFormLabel>
        <CFormTextarea
          className="bia-mono"
          rows={8}
          value={payload}
          onChange={(event) => {
            setPayload(event.target.value)
            state.apisBookingPayload = event.target.value
          }}
        />
      </div>
      <div className="d-flex flex-wrap gap-2">
        <CButton color="success" className="btn-brand" onClick={() => postJson('/sabre/enhancedAirBook')}>
          POST /sabre/enhancedAirBook
        </CButton>
        <CButton color="success" className="btn-brand" onClick={() => postJson('/sabre/enhancedVehBook')}>
          POST /sabre/enhancedVehBook
        </CButton>
        <CButton color="light" onClick={() => apiFetch('POST', '/sabre/enhancedEndTransaction', {})}>
          POST /sabre/enhancedEndTransaction (ER)
        </CButton>
      </div>
    </div>
  )
}

/* ===========================================================================
   Tab CREATE — CHEAT-SHEET INTERACTIVO de comandos host Sabre
   Cada fila tiene: template editable + boton ▶ Ejecutar + ejemplo clickeable.
   Resultado va al panel comun y se loguea en el log de la pestaña.
   =========================================================================== */
function ApiTabCreate({ state, toast }) {
  const [cmd, setCmd] = useState('')
  const [out, setOut] = useState(null) // { text, err }
  const inputRef = useRef(null)
  const total = SABRE_CMD_CATALOG.reduce((s, c) => s + c.cmds.length, 0)

  // Port de csRunCommand (nested en bindApis): ejecuta y muestra el response en el panel.
  async function csRunCommand(command) {
    setOut({ text: '⏳ Ejecutando ' + command + '...', err: false })
    try {
      const r = await apiFetch('GET', '/sabre/executeCommand?command=' + encodeURIComponent(command))
      const txt = typeof r.response === 'string' ? r.response : JSON.stringify(r.response, null, 2)
      setOut({ text: '> ' + command + '\n\n' + txt, err: !r.ok })
    } catch (e) {
      setOut({ text: '✗ Error: ' + (e.message || e), err: true })
    }
  }

  function runFromInput() {
    const c = cmd.trim()
    if (!c) {
      toast('Tipeá un comando antes de ejecutar.', 'warning')
      return
    }
    if (!state.sessionToken) {
      toast('Necesitas un token Sabre activo en la barra superior.', 'warning')
      return
    }
    csRunCommand(c)
  }

  // Click en cualquier card → carga el ejemplo en el input
  function loadExample(ex) {
    setCmd(ex)
    if (inputRef.current) inputRef.current.focus()
  }

  // Click en ▶ → ejecutar ese comando directamente
  function runExample(ex) {
    if (!state.sessionToken) {
      toast('Necesitas un token Sabre activo en la barra superior.', 'warning')
      return
    }
    setCmd(ex)
    csRunCommand(ex)
  }

  return (
    <div className="card-x">
      <div className="bia-card-head">
        <h3>🛠 Cheat-sheet de comandos host Sabre</h3>
        <span className="bia-pill">{total} comandos</span>
      </div>
      <CAlert color="info" className="mt-2">
        Cada comando se ejecuta contra <code>GET /sabre/executeCommand?command=...</code> con el token de la barra superior.
        Tocá el ejemplo para cargarlo, editá los placeholders si querés, y dale ▶ <strong>Ejecutar</strong>. El response queda
        abajo y se loguea en el log de la pestaña.
        {!state.sessionToken ? (
          <>
            <br />
            <strong>⚠ Sin token Sabre activo</strong> — guardalo en la barra superior antes de ejecutar.
          </>
        ) : null}
      </CAlert>

      <div className="bia-row-input" style={{ margin: '10px 0' }}>
        <CFormInput
          ref={inputRef}
          className="bia-mono"
          placeholder="Pegá o tipeá el comando aquí — ej: 115DECBUEMIA"
          style={{ fontSize: 14 }}
          value={cmd}
          onChange={(event) => setCmd(event.target.value)}
        />
        <CButton color="success" className="btn-brand" disabled={!state.sessionToken} onClick={runFromInput}>
          ▶ Ejecutar
        </CButton>
        <CButton
          color="light"
          onClick={() => {
            setCmd('')
            setOut(null)
          }}
        >
          ✕
        </CButton>
      </div>
      {out ? (
        <pre
          style={{
            padding: 12, background: '#0f172a', color: out.err ? '#fca5a5' : '#e2e8f0', borderRadius: 6, fontSize: 12,
            maxHeight: 340, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: '0 0 18px',
          }}
        >
          {out.text}
        </pre>
      ) : null}

      {SABRE_CMD_CATALOG.map((sec) => (
        <div key={sec.section} style={{ marginTop: 18 }}>
          <h4 style={{ margin: '0 0 8px', color: sec.color, fontSize: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: sec.color, color: 'white', padding: '3px 12px', borderRadius: 14, fontSize: 12 }}>
              {sec.section}
            </span>
            <span style={{ color: 'var(--muted)', fontSize: 11.5, fontWeight: 400 }}>{sec.cmds.length} comandos</span>
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {sec.cmds.map((c, idx) => (
              <div
                key={idx}
                role="button"
                style={{
                  padding: '10px 12px', cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 8,
                  borderLeft: `3px solid ${sec.color}`, background: '#fff',
                }}
                onClick={() => loadExample(c.ex)}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <code style={{ fontSize: 12.5, color: sec.color, fontWeight: 600 }}>{c.tpl}</code>
                  <CButton
                    size="sm"
                    color="light"
                    style={{ flexShrink: 0 }}
                    onClick={(event) => {
                      event.stopPropagation()
                      runExample(c.ex)
                    }}
                  >
                    ▶
                  </CButton>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>{c.desc}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
                  ej:{' '}
                  <code style={{ background: 'rgba(34,217,137,.1)', padding: '1px 6px', borderRadius: 3 }}>{c.ex}</code>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

/* ===========================================================================
   Tab FULLRES — Reserva GDS completa (vuelo + hotel + auto) paso a paso
   =========================================================================== */

/** Campo del form (label + hint opcional, mismo layout que .field del original). */
function Field({ label, hint, span, children }) {
  return (
    <div style={span ? { gridColumn: '1 / -1' } : undefined}>
      <CFormLabel>
        {label}
        {hint ? (
          <>
            {' '}
            <small style={{ color: 'var(--muted)' }}>{hint}</small>
          </>
        ) : null}
      </CFormLabel>
      {children}
    </div>
  )
}

const GRID3 = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14 }

function ApiTabFullRes({ state, toast }) {
  // Lazy-init identico al original (apiTabFullRes / bindApis)
  if (!state.apisFake) state.apisFake = generateFakeBookingData()
  if (!state.frFlags) state.frFlags = { hotel: true, car: true }
  const f = state.apisFake

  const [form, setForm] = useState(() => formFromFake(state.apisFake))
  const [preview, setPreview] = useState(null)
  const [run, setRun] = useState(null)

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }))

  // 🎲 Generar otros datos (btn-fr-regen): regenera fantasia y resetea el form (como render() original)
  function regen() {
    state.apisFake = generateFakeBookingData()
    setForm(formFromFake(state.apisFake))
    setPreview(null)
    setRun(null)
    emit()
  }

  // 📋 Ver secuencia (btn-fr-build)
  function buildPreview() {
    const seq = frSequence(frRead(form, state.frFlags))
    setPreview(seq.map((c, i) => `${String(i + 1).padStart(2, ' ')}. ${c}`).join('\n'))
  }

  // ▶ Crear reserva real (btn-fr-run): port completo del flujo paso a paso
  async function runSequence() {
    if (!state.sessionToken) {
      toast('Necesitas un token Sabre activo en la barra superior.', 'warning')
      return
    }
    const d = frRead(form, state.frFlags)
    const seq = frSequence(d)
    // Blacklist: NUNCA confundir un valor que el usuario tipeó con un PNR.
    // Esto evita el caso "el PNR me dice GARCIA y no es".
    const blacklist = [
      d.lname, d.fname, d.title, d.origin, d.dest, d.hCity, d.cCity,
      d.hChain, d.cVendor, d.cCat, d.phCity, d.rcvd,
      // Cadenas de hotel / vendors comunes que aparecen en respuestas
      'HOLIDAY','MARRIOTT','SHERATON','HYATT','BESTWE','AVIS','HERTZ',
      'BUDGET','NATION','ENTERPR','THRIFTY','DOLLAR','ALAMO',
    ]

    const responses = []
    const paint = (banner, tone = 'info') => setRun({ phase: 'running', tone, banner, responses: responses.slice() })
    setRun({ phase: 'running', tone: 'info', banner: '⏳ Ejecutando ' + seq.length + ' comandos contra Sabre...', responses: [] })

    let aborted = null
    // Si HOT* falla, saltar el siguiente 0H1*. Si CF* falla, saltar el siguiente 0C*.
    let skipNextHotelSell = false
    let skipNextCarSell = false
    for (const cmd of seq) {
      // Skip programado por shopper previo fallido
      if (skipNextHotelSell && /^0H\d|^0H1/.test(cmd)) {
        skipNextHotelSell = false
        responses.push({ cmd, txt: '⏭ Omitido: el shopper de hotel (HOT*) falló previamente', ok: true, isWarning: true })
        paint('⏭ Salteado ' + cmd)
        continue
      }
      if (skipNextCarSell && /^0C\d/.test(cmd)) {
        skipNextCarSell = false
        responses.push({ cmd, txt: '⏭ Omitido: el shopper de auto (CF*) falló previamente', ok: true, isWarning: true })
        paint('⏭ Salteado ' + cmd)
        continue
      }
      const entry = await apiFetch('GET', `/sabre/executeCommand?command=${encodeURIComponent(cmd)}`)
      const txt = typeof entry.response === 'string' ? entry.response : JSON.stringify(entry.response || '')
      // Detectar si HOT/CF shopper devolvio error - marcar para skip del siguiente sell
      if (cmd === 'HOT1' || cmd.startsWith('HOT')) {
        if (/COMMAND NO LONGER SUPPORTED|MIGRATE|NOT\s+AUTHORIZED|FORMAT\s+ERROR|UNABLE|INVALID/i.test(txt)) {
          skipNextHotelSell = true
        }
      }
      if (cmd === 'CF1/2' || cmd === 'CF1' || cmd.startsWith('CF')) {
        if (/COMMAND NO LONGER SUPPORTED|MIGRATE|NOT\s+AUTHORIZED|FORMAT\s+ERROR|UNABLE|INVALID|NO\s+RECORD/i.test(txt)) {
          skipNextCarSell = true
        }
      }
      // Si el sell de hotel/auto retorna NR IN PTY o DIRECT CONNECT NOT PROCESSED,
      // lo marcamos como warning pero seguimos.
      // WP a veces devuelve warnings tipo "PRICE QUOTE NOT POSSIBLE" pero no
      // es fatal -- lo marcamos como warning y seguimos.
      // (isWpStep / isErStep del original no participaban de la decision final de ok)
      const isIgnoreStep = cmd === 'I'
      // Hotel/auto pasivo no son criticos para crear el PNR - si fallan,
      // continuamos con el resto del flujo (warning, no fatal). El usuario
      // ve el error en el log pero la reserva del vuelo igual se crea.
      // Hotel/auto shop+sell con cualquier formato son tolerados como warning
      const isHotelStep = cmd.startsWith('HOT') || cmd.startsWith('0H1') || cmd.startsWith('0HHL') || cmd.startsWith('HOD')
      const isCarStep = cmd.startsWith('CF') || cmd.startsWith('0C1') || cmd.startsWith('0CCR') || cmd.startsWith('CC')
      // DOCS / SSR son tolerados como warning (la PCC puede no soportar el formato)
      const isDocsStep = cmd.startsWith('3DOCS') || cmd.startsWith('3SSR')
      const isPassiveSell = isHotelStep || isCarStep || isDocsStep
      // Errores fatales: NUMBER OF NAMES, ITINERARY REQUIRED (sin segmentos
      // validos), FORMAT NO LONGER SUPPORTED, etc.
      const fatalRegex = /UNABLE|INVALID\s|FORMAT\s+ERROR|FORMAT\s+NO\s+LONGER\s+SUPPORTED|MIGRATE\s+TO\s+CONTENT|RESTRICTED|EXPIRED|NOT\s+FOUND\s+IN\s+PNR|UC\d|UN\d|NO\s+ITINERARY|ITINERARY\s+REQUIRED|COMPLETE\s+TRANSACTION|NUMBER\s+OF\s+NAMES\s+NOT\s+EQUAL|CHECK\s+NAME|CHECK\s+SEGMENT|CANNOT\s+(?:END|SAVE)|NEED\s+(?:ADDRESS|PHONE|RECVD|TKT)|USE\s+W-|ADDRESS\s+REQUIRED|PSGR\s+SECURITY\s+DATA|APIS\s+REQUIRED|DOCS\s+REQUIRED|REQUEST\s+PENDING|TELETYPE/i
      const fatal = fatalRegex.test(txt)
      // I (ignore) siempre OK.
      // Passive sells (0HHL/0CCR) toleramos como warning (PCC migrada a CSL).
      // ER, WP y todo lo demas: abortan si el response tiene patron fatal.
      let ok
      if (isIgnoreStep) ok = true
      else if (isPassiveSell) ok = true // tolerar - el PCC puede no soportarlos
      else ok = entry.ok && !fatal
      // Errores blandos de hotel/car que NO abortan: NR IN PTY (no shopper),
      // DIRECT CONNECT NOT PROCESSED (carrier no respondió). Los marcamos warning.
      const isSoftHotelCarError = /NR\s+IN\s+PTY|DIRECT\s+CONNECT\s+NOT\s+PROCESSED|COMMAND\s+NO\s+LONGER\s+SUPPORTED/i.test(txt)
      // Marcar como warning si fue tolerado pero el response tiene error
      const isWarning = ((isPassiveSell || isIgnoreStep) && fatal) || (isPassiveSell && isSoftHotelCarError)
      responses.push({ cmd, txt, ok, isWarning })
      // Render parcial para que el usuario vea progreso
      paint(
        <>
          ⏳ Paso {responses.length}/{seq.length} ·{' '}
          <code style={{ background: '#0f172a', color: '#22d989', padding: '2px 8px', borderRadius: 4 }}>{cmd}</code>
        </>,
      )
      if (!ok) {
        aborted = responses[responses.length - 1]
        break
      }
    }

    // AUTO-FALLBACK: si ER fallo con REQUEST PENDING / TELETYPE, intentamos
    // ET automaticamente (forza end-transaction via teletype, salva la reserva
    // cuando hay direct connect failures de auto/hotel).
    if (aborted && /REQUEST\s+PENDING|TELETYPE/i.test(aborted.txt) && aborted.cmd === 'ER') {
      paint(
        <>
          ⏳ ER quedó pendiente · intentando recovery automático con <code>ET</code>...
        </>,
        'warn',
      )
      try {
        const etEntry = await apiFetch('GET', '/sabre/executeCommand?command=' + encodeURIComponent('ET'))
        const etTxt = typeof etEntry.response === 'string' ? etEntry.response : JSON.stringify(etEntry.response || '')
        const etOk = etEntry.ok && !/UNABLE|ERROR|INVALID|REQUEST\s+PENDING|TELETYPE|REJECTED/i.test(etTxt)
        responses.push({ cmd: 'ET (auto-recovery)', txt: etTxt, ok: etOk, isWarning: !etOk })
        if (etOk) {
          // ET funcionó - desmarcamos el aborted para que se muestre el éxito
          aborted = null
        }
      } catch (e) {
        responses.push({ cmd: 'ET (auto-recovery)', txt: 'Error de red: ' + e.message, ok: false })
      }
    }

    // Buscar el PNR en el response del ER (ultimo comando) usando la blacklist
    let pnr = null
    if (!aborted) {
      const lastOk = [...responses].reverse().find((r) => r.ok)
      if (lastOk) pnr = extractPnrFromResponse(lastOk.txt, blacklist)
      // Tambien probar contra todos los responses (a veces sale en pasos anteriores)
      if (!pnr) {
        for (let i = responses.length - 1; i >= 0; i--) {
          const p = extractPnrFromResponse(responses[i].txt, blacklist)
          if (p) {
            pnr = p
            break
          }
        }
      }
      // Fallback: llamar a /sabre/reservation para que devuelva el PNR estructurado
      if (!pnr) {
        try {
          const r = await apiFetch('GET', '/sabre/reservation')
          const body = typeof r.response === 'string' ? r.response : JSON.stringify(r.response || '')
          pnr = extractPnrFromResponse(body, blacklist)
        } catch (e) {}
      }
      // Ultimo recurso: pedir explicitamente *R (display TODO el PNR) y aplicar
      // regex especifica para encontrar el record locator en la cabecera.
      // *R devuelve algo como:
      //   "MNAVRC/W   ABCDEF  ARGAR 1A  *I9 PEREZ/JUAN ..."
      // donde el segundo token suele ser el locator real.
      if (!pnr) {
        try {
          const r = await apiFetch('GET', '/sabre/executeCommand?command=' + encodeURIComponent('*R'))
          const body = typeof r.response === 'string' ? r.response : JSON.stringify(r.response || '')
          // Patron tipico al inicio: "1.1<NAME>" precedido por un locator.
          // Buscar las primeras lineas del PNR.
          const head = body.slice(0, 800)
          // a) Patron "ABCDEF" seguido de "1.1" del primer pasajero
          let m = head.match(/\b([A-Z][A-Z0-9]{5})\b[\s\S]{0,80}?1\.1[A-Z]/)
          if (m && extractPnrFromResponse(m[1], blacklist)) pnr = m[1]
          // b) Patron "RP/PCC/...  ABCDEF AGENTE..."
          if (!pnr) {
            m = head.match(/RP\/[A-Z0-9]+\/[^\n]*?\b([A-Z][A-Z0-9]{5})\b/)
            if (m && extractPnrFromResponse(m[1], blacklist)) pnr = m[1]
          }
          // c) Fallback general
          if (!pnr) pnr = extractPnrFromResponse(body, blacklist)
        } catch (e) {}
      }
      // Ultimo recurso ABSOLUTO: *A
      if (!pnr) {
        try {
          const r = await apiFetch('GET', '/sabre/executeCommand?command=' + encodeURIComponent('*A'))
          const body = typeof r.response === 'string' ? r.response : JSON.stringify(r.response || '')
          pnr = extractPnrFromResponse(body, blacklist)
        } catch (e) {}
      }
    }

    if (aborted) setRun({ phase: 'aborted', aborted, responses: responses.slice() })
    else if (pnr) setRun({ phase: 'success', pnr, responses: responses.slice() })
    else setRun({ phase: 'nopnr', responses: responses.slice() })
  }

  const checkLabelStyle = { fontSize: 12, color: 'var(--muted)', fontWeight: 400, display: 'flex', gap: 6, alignItems: 'center', marginBottom: 0 }

  return (
    <div className="card-x">
      <div className="bia-card-head">
        <h3>
          Reserva GDS completa{' '}
          <small style={{ color: 'var(--muted)', fontWeight: 400 }}>— vuelo + hotel + auto en una sola secuencia nativa Sabre</small>
        </h3>
        <CButton size="sm" color="light" onClick={regen}>
          🎲 Generar otros datos
        </CButton>
      </div>

      <CAlert color="info" className="mt-2">
        Reserva REAL con la secuencia nativa Sabre: <strong>1</strong> disponibilidad → <strong>0</strong> venta →{' '}
        <strong>HOT/0HHL</strong> hotel → <strong>CF/0CCR</strong> auto → <strong>5 elementos</strong> (P-R-I-N-T) →{' '}
        <strong>WP</strong> cotizar → <strong>ER</strong> guardar. Datos precargados con fantasía (hoy + 3 meses).
      </CAlert>

      <h4 style={{ margin: '14px 0 6px', color: 'var(--brand-dark)' }}>✈️ Vuelo</h4>
      <div style={GRID3}>
        <Field label="Fecha ida (DDMMM)">
          <CFormInput className="bia-mono" maxLength={5} value={form.date} onChange={set('date')} />
        </Field>
        <Field label="Origen" hint={`(${f.originName})`}>
          <CFormInput className="bia-mono" maxLength={3} value={form.origin} onChange={set('origin')} />
        </Field>
        <Field label="Destino" hint={`(${f.destName})`}>
          <CFormInput className="bia-mono" maxLength={3} value={form.dest} onChange={set('dest')} />
        </Field>
        <Field label="Fecha vuelta (opcional)">
          <CFormInput className="bia-mono" maxLength={5} placeholder="DDMMM" value={form.rdate} onChange={set('rdate')} />
        </Field>
        <Field label="Hora">
          <CFormInput className="bia-mono" value={form.time} onChange={set('time')} />
        </Field>
        <Field label="Aerolinea (opcional)">
          <CFormInput className="bia-mono" maxLength={2} placeholder="AA, IB, LA..." value={form.airline} onChange={set('airline')} />
        </Field>
        <Field label="Clase">
          <CFormInput className="bia-mono" maxLength={2} value={form.cls} onChange={set('cls')} />
        </Field>
        <Field label="Asientos">
          <CFormInput type="number" min={1} value={form.seats} onChange={set('seats')} />
        </Field>
      </div>

      <h4 style={{ margin: '18px 0 6px', color: 'var(--brand-dark)', display: 'flex', alignItems: 'center', gap: 10 }}>
        🏨 Hotel
        <label style={checkLabelStyle}>
          <input
            type="checkbox"
            checked={!!state.frFlags.hotel}
            onChange={(event) => {
              state.frFlags.hotel = event.target.checked
              emit()
            }}
          />{' '}
          incluir en la reserva
        </label>
      </h4>
      <div style={state.frFlags.hotel ? GRID3 : { ...GRID3, opacity: 0.4, pointerEvents: 'none' }}>
        <Field label="Cadena (2-3 chars)">
          <CFormInput className="bia-mono" maxLength={3} value={form.hChain} onChange={set('hChain')} />
        </Field>
        <Field label="Ciudad">
          <CFormInput className="bia-mono" maxLength={3} value={form.hCity} onChange={set('hCity')} />
        </Field>
        <Field label="Cadena" hint={`(${f.hotelChainName})`}>
          <CFormInput readOnly value={f.hotelChainName} />
        </Field>
        <Field label="Check-in (DDMMM)">
          <CFormInput className="bia-mono" maxLength={5} value={form.hCheckin} onChange={set('hCheckin')} />
        </Field>
        <Field label="Check-out (DDMMM)">
          <CFormInput className="bia-mono" maxLength={5} value={form.hCheckout} onChange={set('hCheckout')} />
        </Field>
        <Field label="Habitaciones">
          <CFormInput type="number" min={1} value={form.hRooms} onChange={set('hRooms')} />
        </Field>
      </div>

      <h4 style={{ margin: '18px 0 6px', color: 'var(--brand-dark)', display: 'flex', alignItems: 'center', gap: 10 }}>
        🚗 Auto
        <label style={checkLabelStyle}>
          <input
            type="checkbox"
            checked={!!state.frFlags.car}
            onChange={(event) => {
              state.frFlags.car = event.target.checked
              emit()
            }}
          />{' '}
          incluir en la reserva
        </label>
      </h4>
      <div style={state.frFlags.car ? GRID3 : { ...GRID3, opacity: 0.4, pointerEvents: 'none' }}>
        <Field label="Vendor (2 chars)">
          <CFormInput className="bia-mono" maxLength={2} value={form.cVendor} onChange={set('cVendor')} />
        </Field>
        <Field label="Ciudad/aeropuerto">
          <CFormInput className="bia-mono" maxLength={3} value={form.cCity} onChange={set('cCity')} />
        </Field>
        <Field label="Vendor" hint={`(${f.carVendorName})`}>
          <CFormInput readOnly value={f.carVendorName} />
        </Field>
        <Field label="Pickup (DDMMM)">
          <CFormInput className="bia-mono" maxLength={5} value={form.cPickup} onChange={set('cPickup')} />
        </Field>
        <Field label="Return (DDMMM)">
          <CFormInput className="bia-mono" maxLength={5} value={form.cReturn} onChange={set('cReturn')} />
        </Field>
        <Field label="Categoria (ICAR/CCAR/etc)">
          <CFormInput className="bia-mono" maxLength={4} value={form.cCat} onChange={set('cCat')} />
        </Field>
        <Field label="Hora pickup (HHMM)">
          <CFormInput className="bia-mono" maxLength={4} value={form.cPickupTime} onChange={set('cPickupTime')} />
        </Field>
        <Field label="Hora return (HHMM)">
          <CFormInput className="bia-mono" maxLength={4} value={form.cReturnTime} onChange={set('cReturnTime')} />
        </Field>
      </div>

      <h4 style={{ margin: '18px 0 6px', color: 'var(--brand-dark)' }}>👤 Pasajero y contacto</h4>
      <div style={GRID3}>
        <Field label="Apellido">
          <CFormInput value={form.lname} onChange={set('lname')} />
        </Field>
        <Field label="Nombre">
          <CFormInput value={form.fname} onChange={set('fname')} />
        </Field>
        <Field label="Titulo">
          <CFormInput maxLength={3} value={form.title} onChange={set('title')} />
        </Field>
        <Field label="Telefono">
          <CFormInput className="bia-mono" value={form.phone} onChange={set('phone')} />
        </Field>
        <Field label="Ciudad tel (3 chars)">
          <CFormInput className="bia-mono" maxLength={3} value={form.phCity} onChange={set('phCity')} />
        </Field>
        <Field label="Received from">
          <CFormInput value={form.rcvd} onChange={set('rcvd')} />
        </Field>
        <Field label="Email (PE)">
          <CFormInput className="bia-mono" value={form.email} onChange={set('email')} />
        </Field>
        <Field label="Celular pax (11xxxxxxxx)">
          <CFormInput className="bia-mono" value={form.celular} onChange={set('celular')} />
        </Field>
        <Field label="DNI">
          <CFormInput className="bia-mono" value={form.dni} onChange={set('dni')} />
        </Field>
        <Field label="CUIL">
          <CFormInput className="bia-mono" value={form.cuil} onChange={set('cuil')} />
        </Field>
        <Field label="Direccion (W-)" hint="— tu PCC puede exigirla antes de ER" span>
          <CFormInput value={form.addr} onChange={set('addr')} />
        </Field>
      </div>

      <h4 style={{ margin: '18px 0 6px', color: 'var(--brand-dark)', display: 'flex', alignItems: 'center', gap: 10 }}>
        🛂 DOCS / APIS (pasaporte)
        <label style={checkLabelStyle}>
          <input
            type="checkbox"
            checked={form.docsOn}
            onChange={(event) => setForm((prev) => ({ ...prev, docsOn: event.target.checked }))}
          />{' '}
          incluir en la reserva (recomendado para internacionales)
        </label>
      </h4>
      <div style={GRID3}>
        <Field label="Pais emisor">
          <CFormInput className="bia-mono" maxLength={3} value={form.docsCountry} onChange={set('docsCountry')} />
        </Field>
        <Field label="Nro pasaporte">
          <CFormInput className="bia-mono" maxLength={10} value={form.docsPassport} onChange={set('docsPassport')} />
        </Field>
        <Field label="Sexo">
          <CFormSelect value={form.docsSex} onChange={set('docsSex')}>
            <option value="M">M</option>
            <option value="F">F</option>
          </CFormSelect>
        </Field>
        <Field label="Fecha nacimiento (DDMMMYY)">
          <CFormInput className="bia-mono" maxLength={7} value={form.docsDob} onChange={set('docsDob')} />
        </Field>
        <Field label="Vencimiento pasaporte (DDMMMYY)">
          <CFormInput className="bia-mono" maxLength={7} value={form.docsExp} onChange={set('docsExp')} />
        </Field>
        <Field label="Carrier" hint="(YY = todos)">
          <CFormInput className="bia-mono" maxLength={2} value={form.docsCarrier} onChange={set('docsCarrier')} />
        </Field>
      </div>

      <div className="d-flex flex-wrap gap-2" style={{ marginTop: 18 }}>
        <CButton color="light" onClick={buildPreview}>
          📋 Ver secuencia (preview)
        </CButton>
        <CButton
          color="success"
          className="btn-brand"
          disabled={!state.sessionToken}
          title={state.sessionToken ? undefined : 'Falta token Sabre en la barra superior'}
          onClick={runSequence}
        >
          ▶ Crear reserva real (vuelo + hotel + auto)
        </CButton>
      </div>
      {preview ? (
        <pre className="bia-pre" style={{ marginTop: 14, whiteSpace: 'pre-wrap' }}>
          {preview}
        </pre>
      ) : null}
      <FrResult run={run} toast={toast} />
    </div>
  )
}

/* ===== Vista principal (port de viewApis + renderApiTab) ===== */

function renderApiTab(tab, state, toast) {
  if (tab === 'auth') return <ApiTabAuth state={state} />
  if (tab === 'pnr') return <ApiTabPnr state={state} toast={toast} />
  if (tab === 'booking') return <ApiTabBooking state={state} toast={toast} />
  if (tab === 'fullres') return <ApiTabFullRes state={state} toast={toast} />
  if (tab === 'create') return <ApiTabCreate state={state} toast={toast} />
  return null
}

function ApisView() {
  const state = useBostonState()
  const { toast } = useUI()
  const tab = state.apisTab

  return (
    <>
      <div className="page-head">
        <div>
          <h1>APIs Sabre</h1>
          <p>
            Endpoints del backend Boston (<code>{state.config.bostonApi}</code>). Unifica las operaciones que estaban en{' '}
            <code>sabre-api-console.html</code>.
          </p>
        </div>
        <div>
          {state.sessionToken ? (
            <span className="bia-badge ok">🔐 Token Workspace activo</span>
          ) : (
            <span className="bia-badge warn">⚠ Sin token de Workspace · usando API key</span>
          )}
        </div>
      </div>

      <CNav variant="tabs" className="mb-3">
        {API_TABS.map((t) => (
          <CNavItem key={t.id}>
            <CNavLink
              active={tab === t.id}
              role="button"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                state.apisTab = t.id
                emit()
              }}
            >
              {t.label}
            </CNavLink>
          </CNavItem>
        ))}
      </CNav>

      {renderApiTab(tab, state, toast)}

      <div className="card-x">
        <div className="bia-card-head">
          <h3>Log de llamadas</h3>
          <CButton
            size="sm"
            color="light"
            onClick={() => {
              state.apisLog = []
              emit()
            }}
          >
            Limpiar
          </CButton>
        </div>
        {state.apisLog.length === 0 ? (
          <p className="bia-empty">Sin llamadas todavia.</p>
        ) : (
          <div className="bia-log">
            {state.apisLog.map((l, i) => (
              <div key={i} className={`bia-log-row ${l.ok ? 'ok' : 'err'}`}>
                <div className="bia-log-meta">
                  <span className="bia-log-cmd">
                    {l.method} {String(l.url).replace(state.config.bostonApi.replace(/\/$/, ''), '')}
                  </span>
                  <span className="bia-log-time">
                    · {l.status} · {l.ms} ms
                  </span>
                </div>
                <pre>
                  {typeof l.response === 'string'
                    ? l.response.slice(0, 1200)
                    : JSON.stringify(l.response, null, 2).slice(0, 1200)}
                </pre>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default ApisView
